/**
 * DeleteLavanderiaModal — modal de eliminación con reasignación.
 *
 * Se abre desde `LavanderiasTab` cuando el admin pulsa "Eliminar" sobre una
 * lavandería que tiene dependencias (propiedades, lavados o gastos
 * operacionales). El admin debe elegir una lavandería destino: el backend
 * reasigna **todo** (propiedades + lavados + gastos) en una transacción y
 * recién después borra la lavandería origen.
 *
 * UX clave:
 *  - Resumen de impacto arriba (cuántas propiedades, lavados, gastos).
 *  - Listado de propiedades afectadas (lazy load si no llega cacheado).
 *  - Selector de lavandería destino con tag "Sistema" para identificar la
 *    genérica (que también es destino válido).
 *  - Aviso explícito sobre la regla de gastos en colisión (UNIQUE por
 *    lavanderia_id + periodo): si el destino ya tiene un gasto en ese mes,
 *    el del origen se descarta. Lo decidió así el owner — es destructivo
 *    por diseño y queda escrito en pantalla para evitar sorpresas.
 *  - Botón final "Eliminar y reasignar" en estilo danger; cancelar a la
 *    izquierda.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  List,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import { HomeOutlined, WarningOutlined } from "@ant-design/icons";
import { finanzasEmpresaApi } from "../../services/finanzasEmpresa.api";
import { getMsg } from "../../utils/antdStatic";
import { formatPropertyName } from "../../utils/propertyDisplay";

const { Text, Paragraph } = Typography;

type LavanderiaRow = {
  id: number;
  nombre: string;
  direccion?: string;
  descripcion?: string;
  activo: boolean;
  es_generica?: boolean;
  propiedades_count?: number;
  lavados_count?: number;
  gastos_count?: number;
    reembolsos_count?: number;
};

type PropiedadAsociada = {
  id: number;
  nombrepropiedad: string;
  habilitado: boolean;
  id_cliente: number | null;
  cliente: string | null;
};

interface Props {
  target: LavanderiaRow;
  /** Lavanderías candidatas a destino (excluyendo la actual y las inactivas). */
  destinos: LavanderiaRow[];
  /** Lista de propiedades de target precargada por LavanderiasTab si la fila estaba expandida. */
  propiedadesCached?: PropiedadAsociada[];
  onClose: () => void;
  onDeleted: () => void;
}

export default function DeleteLavanderiaModal({
  target,
  destinos,
  propiedadesCached,
  onClose,
  onDeleted,
}: Props) {
  const [destinoId, setDestinoId] = useState<number | null>(null);
  const [propiedades, setPropiedades] = useState<PropiedadAsociada[] | null>(
    propiedadesCached ?? null,
  );
  const [loadingProps, setLoadingProps] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const propCount = target.propiedades_count ?? 0;
  const lavadoCount = target.lavados_count ?? 0;
  const gastosCount = target.gastos_count ?? 0;
  const reembolsosCount = target.reembolsos_count ?? 0;

  // Si el caller no nos pasó la lista (la fila no estaba expandida cuando el
  // admin clickeó eliminar), la pedimos al abrir. Igual sirve para refrescar
  // si el cacheado quedó stale por un rato.
  useEffect(() => {
    if (propiedadesCached) {
      setPropiedades(propiedadesCached);
      return;
    }
    if (propCount === 0) {
      setPropiedades([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingProps(true);
      const res = await finanzasEmpresaApi.getLavanderiaPropiedades(target.id);
      if (!cancelled) {
        setPropiedades(res?.data ?? []);
        setLoadingProps(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [target.id, propiedadesCached, propCount]);

  const destino = useMemo(
    () => destinos.find((d) => d.id === destinoId) ?? null,
    [destinos, destinoId],
  );

  const handleConfirm = async () => {
    if (destinoId == null) {
      getMsg()?.warning("Elige una lavandería destino para reasignar los registros.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await finanzasEmpresaApi.deleteLavanderia(target.id, {
        reasignar_a: destinoId,
      });
      // El backend devuelve un objeto `{ success, message, reasignados }`
      // cuando hay reasignación. El toast lo armamos acá para detallar el
      // destino y el conteo, en vez del genérico de apiFetch.
      if (res?.success) {
        getMsg()?.success(
          `Lavandería eliminada. ${propCount} propiedad${propCount !== 1 ? "es" : ""}, ` +
            `${lavadoCount} lavado${lavadoCount !== 1 ? "s" : ""} y ` +
            `${gastosCount} gasto${gastosCount !== 1 ? "s" : ""} reasignados a "${destino?.nombre}".`,
        );
        onDeleted();
      }
      // Si no hubo `success` es porque apiFetch atrapó el error y mostró
      // toast — no cerramos el modal, dejamos que el admin reintente.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      title={
        <Space>
          <WarningOutlined style={{ color: "#ff4d4f" }} />
          Eliminar lavandería: {target.nombre}
        </Space>
      }
      onCancel={onClose}
      width={680}
      maskClosable={!submitting}
      closable={!submitting}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>,
        <Button
          key="delete"
          type="primary"
          danger
          loading={submitting}
          disabled={destinoId == null}
          onClick={handleConfirm}
        >
          Eliminar y reasignar
        </Button>,
      ]}
    >
      <Alert
        type="warning"
        showIcon
        message="Esta lavandería tiene registros asociados"
        description={
          <div>
            <Paragraph style={{ marginBottom: 4 }}>
              Si la eliminas, todos sus registros se reasignarán a la lavandería
              destino que elijas abajo. La operación es transaccional: si algo
              falla, no se cambia nada.
            </Paragraph>
            <Space wrap size={[8, 4]}>
              <Tag color="blue" icon={<HomeOutlined />}>
                {propCount} propiedad{propCount !== 1 ? "es" : ""}
              </Tag>
              <Tag color="green">
                {lavadoCount} lavado{lavadoCount !== 1 ? "s" : ""}
              </Tag>
              <Tag color="orange">
                {gastosCount} gasto{gastosCount !== 1 ? "s" : ""} operacional
                {gastosCount !== 1 ? "es" : ""}
              </Tag>
              {reembolsosCount > 0 && <Tag color="purple">{reembolsosCount} reembolso{reembolsosCount !== 1 ? "s" : ""}</Tag>}
            </Space>
          </div>
        }
        style={{ marginBottom: 16 }}
      />

      {/* Selector de destino */}
      <div style={{ marginBottom: 16 }}>
        <Text strong>Lavandería destino</Text>
        <Select
          style={{ width: "100%", marginTop: 6 }}
          placeholder="Elige la lavandería que recibirá los registros"
          value={destinoId ?? undefined}
          onChange={(v) => setDestinoId(v)}
          disabled={submitting}
          options={destinos.map((d) => ({
            value: d.id,
            label: (
              <Space>
                {d.nombre}
                {d.es_generica && <Tag color="blue">Sistema</Tag>}
              </Space>
            ),
          }))}
          notFoundContent={
            destinos.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No hay otras lavanderías activas. Crea una primero o reactiva alguna desde la tabla."
              />
            ) : null
          }
        />
        {gastosCount > 0 && (
          <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
            Nota: los gastos operacionales tienen un único registro por mes. Si
            el destino ya tiene un gasto en alguno de esos meses, los del origen
            se descartarán para evitar duplicados.
          </Text>
        )}
      </div>

      {/* Lista de propiedades afectadas */}
      {propCount > 0 && (
        <div>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            <HomeOutlined /> Propiedades que se reasignarán
          </Text>
          {loadingProps ? (
            <div style={{ padding: 12, textAlign: "center" }}>
              <Spin size="small" /> <Text type="secondary">Cargando propiedades…</Text>
            </div>
          ) : propiedades && propiedades.length > 0 ? (
            <List
              size="small"
              bordered
              style={{ maxHeight: 240, overflow: "auto" }}
              dataSource={propiedades}
              renderItem={(p) => (
                <List.Item>
                  <Space>
                    <span style={{ fontWeight: 500 }}>
                      {formatPropertyName(p.nombrepropiedad)}
                    </span>
                    {!p.habilitado && <Tag color="warning">deshabilitada</Tag>}
                    {p.cliente && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        — {p.cliente}
                      </Text>
                    )}
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <Text type="secondary">Sin propiedades para mostrar.</Text>
          )}
        </div>
      )}
    </Modal>
  );
}
